import {memo, useCallback, useEffect, useState} from 'react';

import styled from 'styled-components';
import {isEmpty} from 'lodash';
import {useSelector} from 'react-redux';

import {pushRoute} from '~/shared/router';
import {selectUserReportOrderDetails} from '~/shared/store/selectors';
import BarCodeCouponComponent from '~/common/components/CouponBarCodeComponent';
import apiService from '~/shared/services/apiService';
import {UserVoucher} from '~/shared/store/models/User/UserVouchers';
import {UserReportOrder} from '~/shared/store/models';
import {useSideMenuHandlers} from '~/common/hooks/UserTransactions';
import {createLogger} from '~/shared/logging';
import {DiscountBanner} from '~/shared/components/UserTransactions/shared';
import {getLocalizationService} from '~/shared/services/localisationService';

import {Subheader, Order} from './components';

const logger = createLogger('ShowOrder');

const BarCodeContainer = styled.div`
  padding: 5px;
`;

const TerminalDiscountMessage = styled(DiscountBanner)`
  margin: 0 16px 16px 16px;
`;

const MY_ORDERS_URL = '/personal-area/?section=my-orders';

const ShowOrder = ({backButtonText, checkIfMoneycardIsCredit}: {backButtonText: string; checkIfMoneycardIsCredit: (moneycardId: number) => boolean}) => {
  const {t} = getLocalizationService();
  const orderDetails = useSelector(selectUserReportOrderDetails);
  const [barCode, setBarCode] = useState<UserVoucher>();
  const menuActions = useSideMenuHandlers();
  const [openedRowMenuId, setOpenedRowMenuId] = useState<number>();

  const onMenuClick = useCallback(
    (rowId?: number) => {
      if (openedRowMenuId === rowId || !rowId) {
        setOpenedRowMenuId(undefined);
      } else {
        setOpenedRowMenuId(rowId);
      }
    },
    [openedRowMenuId],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    const {isBarCodeOrder, isCanceled, id, orderTotal, restaurantId} = orderDetails;
    const getBarcode = async () => {
      const {data} = await apiService.getBarcodeOrder({orderId: id, orderTotal, resId: restaurantId});
      if (data.vouchers[0]) {
        setBarCode(data.vouchers[0]);
      }
    };
    if (!isBarCodeOrder || isCanceled) {
      return;
    }
    getBarcode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const userDoesntHaveOrderDetailsSaved = Boolean(orderDetails) !== false && isEmpty(orderDetails);
    /**
     * showOrder is saved in the persist store once clicking on a transaction from the use report (mobile only),
     * as the persist store loads after the first render, the object orderDetails will initiate as false,
     * incase the the user doesn't have saved data (empty object is returned which is decided by the persistConfig)
     * he will be redirected back to the user report.
     */

    if (userDoesntHaveOrderDetailsSaved) {
      logger.warn('user doesnt have order details saved, redirecting to user report');
      pushRoute(MY_ORDERS_URL, {keepAllQueries: false});
    }
  }, [orderDetails]);

  return (
    <>
      <Subheader
        order={orderDetails}
        openedRowMenuId={openedRowMenuId}
        menuActions={menuActions}
        onMenuClick={onMenuClick}
        baseUrl={MY_ORDERS_URL}
        backButtonText={backButtonText}
      />
      {barCode ? (
        <BarCodeContainer>
          <BarCodeCouponComponent restaurantName={orderDetails.restaurantName} barCode={barCode} />
        </BarCodeContainer>
      ) : (
        <Order order={orderDetails as UserReportOrder} shouldShowCommentsRows={false} checkIfMoneycardIsCredit={checkIfMoneycardIsCredit} />
      )}

      {(orderDetails.orderTypeOrigin === 'terminal' && Number(orderDetails?.discount) > 0) && (
        <TerminalDiscountMessage>{t('in_this_order_you_recived_xxxx_discount', {discount: orderDetails.discount})}</TerminalDiscountMessage>
      )}
    </>
  );
};

export default memo(ShowOrder);
